import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // View Model always starts OFF (product image) on a fresh page load —
    // the state is deliberately NOT persisted across pages (2026-08-12; the
    // earlier sessionStorage restore made later PLPs land with it ON). The
    // one exception is the PLP back-nav snapshot (RE-57591 follow-up): the
    // inline restore in main-collection.liquid re-flags this element before
    // it upgrades, and the restored cards get their model slides as they
    // enter the viewport.
    if (this.viewModelOn) {
      // Upgrades run in tree order, so the cards' slideshows are NOT yet
      // initialised here: prime them with `initial-slide` (honoured by their
      // own init) and let the in-viewport observer settle the rest.
      this.#applyViewModel();
      const { grid } = this.refs;
      if (grid) this.#primeModelSlides([grid]);
      this.#observeViewportEntries();
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Whether the View Model (on-model / slide 1) view is currently active.
   * @returns {boolean}
   */
  get viewModelOn() {
    return this.getAttribute('data-view-model') === 'on';
  }

  /**
   * Toggles the global "View Model" state for every product card in the grid.
   * OFF (default) shows the product image (slide 0); ON shows the card's
   * dedicated model slide — rendered by card-gallery.liquid from the
   * custom.model_image product metafield (slide-id "model-image"). Cards
   * without a model image stay on their product image. Bound from the toggle
   * button via `on:click="results-list/toggleViewModel"`.
   *
   * @param {Event} event
   */
  toggleViewModel = ({ target }) => {
    const next = !this.viewModelOn;
    this.#setViewModel(next);

    // Keep all View Model toggles in this section in sync (there can be more
    // than one instance rendered, e.g. desktop vs. a duplicated control).
    const toggles = this.querySelectorAll('.view-model-toggle');
    for (const toggle of toggles) {
      toggle.setAttribute('aria-pressed', String(next));
    }

    if (target instanceof HTMLElement) target.setAttribute('aria-pressed', String(next));
  };

  /**
   * Sets the View Model state and applies it to all current cards.
   * @param {boolean} on
   */
  #setViewModel(on) {
    this.setAttribute('data-view-model', on ? 'on' : 'off');
    this.#applyViewModel();
  }

  /**
   * Applies the current View Model state to the product card slideshows that
   * are in (or within 100px of) the viewport — Horizon's `in-viewport`
   * attribute — and leaves the rest to the in-viewport observer, which applies
   * the state to each card as it scrolls in. Switching used to select every
   * loaded card at once: ~15–150ms per VIDEO product (reveal of the hidden
   * model slide + layout + the card's video pause/rewind), i.e. a 1–3s
   * main-thread block on a 24–72 card grid (Laure 2026-10-07, "takes a very
   * long time to switch"). Cards without a model slide keep their product
   * image; OFF returns every card to slide 0 the same lazy way.
   */
  #applyViewModel() {
    const { grid } = this.refs;
    if (!grid) return;

    const slideshows = /** @type {NodeListOf<import('slideshow').Slideshow>} */ (
      grid.querySelectorAll('product-card slideshow-component[in-viewport]')
    );
    for (const slideshow of slideshows) this.#applyToSlideshow(slideshow);

    this.#observeViewportEntries();
  }

  /**
   * Puts one card slideshow on the slide the current View Model state wants:
   * the model slide when ON (select({id}) reveals it on video products, then
   * the scroller position is checked — select() returns early when `current`
   * already points at the model though the scroller never moved), slide 0
   * when OFF. No-op for cards without a model slide, and for slideshows that
   * haven't upgraded yet (they take `initial-slide` from #primeModelSlides).
   * @param {import('slideshow').Slideshow} slideshow
   */
  #applyToSlideshow(slideshow) {
    if (!slideshow.isConnected || !slideshow.refs?.scroller) return;
    const rawIndex = this.#modelSlideIndex(slideshow);
    if (rawIndex < 1) return;
    const { scroller } = slideshow.refs;
    const target = this.viewModelOn ? slideshow.refs.slides?.[rawIndex] : slideshow.refs.slides?.[0];
    if (!target) return;
    if (slideshow.slides?.[slideshow.current] !== target) {
      if (this.viewModelOn) slideshow.select({ id: 'model-image' }, undefined, { animate: false });
      else slideshow.select(0, undefined, { animate: false });
    }
    const left = target.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
    if (Math.abs(scroller.scrollLeft - left) > 1) scroller.scrollTo({ left, behavior: 'instant' });
  }

  /**
   * Re-apply the current View Model state to cards appended by infinite scroll
   * or Load More, so newly loaded pages match the rest of the grid.
   *
   * RE-57591 (2026-10-07): appended cards below the fold have no `in-viewport`
   * attribute yet (SlideshowViewportObserver adds it ~100px before a slideshow
   * scrolls into view; until then its scroller is overflow: hidden, no
   * compositor layer — the iOS crash guard), so a select() issued at append
   * time leaves the scroller parked on slide 0 and the card shows the ghost
   * until a hover re-selects. Never reproduces in an occluded QA window (its
   * IntersectionObservers never fire, so every card stays "in viewport"). So:
   * set `initial-slide` for the cards that are already in view, and re-assert
   * the model slide the moment each remaining card gains `in-viewport`
   * (attribute observer on the grid) — select() plus a scroll-position check,
   * because select() returns early when `current` already points at the
   * model slide even though the scroller never moved.
   *
   * @param {NodeListOf<Element> | Element[]} [items]
   */
  afterPageRender(items) {
    if (!this.viewModelOn) return;
    this.#primeModelSlides(items ?? []);
    this.#observeViewportEntries();
  }

  /**
   * Sets `initial-slide` to the model slide on every card slideshow under
   * `roots` (an uninitialised slideshow honours it in its own init; an
   * initialised one re-selects on the attribute change) and asserts the
   * ones already in view.
   * @param {Iterable<Element>} roots
   */
  #primeModelSlides(roots) {
    for (const root of roots) {
      const slideshows = /** @type {NodeListOf<import('slideshow').Slideshow>} */ (
        root.querySelectorAll('product-card slideshow-component')
      );

      for (const slideshow of slideshows) {
        const rawIndex = this.#modelSlideIndex(slideshow);
        if (rawIndex < 1) continue;
        slideshow.setAttribute('initial-slide', String(rawIndex));
        if (slideshow.hasAttribute('in-viewport')) this.#applyToSlideshow(slideshow);
      }
    }
  }

  /** @type {MutationObserver | null} */
  #viewportEntryObserver = null;

  /**
   * Watches the grid for slideshows gaining `in-viewport` and applies the
   * current View Model state to each as it scrolls in (model slide when ON,
   * slide 0 when OFF).
   */
  #observeViewportEntries() {
    if (this.#viewportEntryObserver) return;
    const { grid } = this.refs;
    if (!grid) return;
    this.#viewportEntryObserver = new MutationObserver((records) => {
      for (const record of records) {
        const target = /** @type {Element} */ (record.target);
        if (!target.hasAttribute('in-viewport') || !target.closest('product-card')) continue;
        const slideshow = /** @type {import('slideshow').Slideshow} */ (target);
        // Now, next frame and after the slideshow's own visibility sync has
        // settled — idempotent, so the repeats are free when the first lands.
        this.#applyToSlideshow(slideshow);
        requestAnimationFrame(() => this.#applyToSlideshow(slideshow));
        setTimeout(() => this.#applyToSlideshow(slideshow), 300);
      }
    });
    this.#viewportEntryObserver.observe(grid, { attributes: true, attributeFilter: ['in-viewport'], subtree: true });
  }

  /**
   * @param {import('slideshow').Slideshow} slideshow
   * @returns {number} raw index of the model slide among ALL slides (hidden
   *   included — on video products it renders [hidden]; select({id}) reveals it), or -1.
   */
  #modelSlideIndex(slideshow) {
    // `refs` only exists once the slideshow has upgraded; before that, read
    // the slide elements straight from the DOM (same order as refs.slides).
    const allSlides = slideshow.refs?.slides ?? [...slideshow.querySelectorAll('slideshow-slide')];
    if (!allSlides || allSlides.length < 2) return -1;
    return allSlides.findIndex((slide) => slide.getAttribute('slide-id') === 'model-image');
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
